


import React, { useState } from "react";
import { Link, Redirect, useHistory } from "react-router-dom";
import { Helmet } from "react-helmet";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { login } from "../store/actions/auth";
import axios from "axios";
import { IoShieldCheckmarkOutline, IoAlertCircleOutline } from "react-icons/io5";
import { FiCheck } from "react-icons/fi";
import { FcGoogle } from "react-icons/fc";
import { FaApple, FaLock } from "react-icons/fa";

const Login = ({ login, isAuthenticated }) => {
  const history = useHistory();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [alert, setAlert] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { email, password } = formData;

  const onChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    console.log("Sending request to API:", { email, password });
    setIsSubmitting(true);

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/cust_api/user_login`,
        { email, password }
      );

      if (res.data && res.data.user) {
        const {
          _id,
          token,
          full_name,
          mobile_number,
          email: userEmail,
          property_owner_type,
        } = res.data.user;

        // Save login data to session storage
        sessionStorage.setItem("AccessToken", _id);
        sessionStorage.setItem("token", token);
        sessionStorage.setItem("connect_to_name", full_name);
        sessionStorage.setItem("connect_to_no", mobile_number);
        sessionStorage.setItem("connect_to_email", userEmail);
        sessionStorage.setItem("property_owner_type", property_owner_type);

        // Fetch user profile after successful login
        const profileResponse = await axios.post(
          `${process.env.REACT_APP_API_URL}/cust_api/get_profile`,
          {
            user_id: _id,
          }
        );

        const {
          free_view_count,
          free_post_count,
          paid_view_count,
          paid_post_count,
          feature_count,
          offer_count,
          upcoming_project_count,
        } = profileResponse.data.count_data || {};

        // Store counts in session storage
        sessionStorage.setItem("free_view_count", free_view_count);
        sessionStorage.setItem("free_post_count", free_post_count);
        sessionStorage.setItem("paid_view_count", paid_view_count);
        sessionStorage.setItem("paid_post_count", paid_post_count);
        sessionStorage.setItem("feature_count", feature_count);
        sessionStorage.setItem("offer_count", offer_count);
        sessionStorage.setItem(
          "upcoming_project_count",
          upcoming_project_count
        );

        console.log(
          "Stored Free Post Count:",
          sessionStorage.getItem("free_post_count")
        );

        login(res.data);
        setAlert({ msg: "Login successful! Welcome back!", type: "success" });

        // Redirect after a short delay
        setTimeout(() => {
          history.push("/");
        }, 2000);
      } else {
        setAlert({
          msg: "Login failed: Invalid response from server",
          type: "danger",
        });
      }
    } catch (error) {
      console.error(
        "Login error:",
        error.response ? error.response.data : error.message
      );
      const errorMessage =
        error.response && error.response.data.message
          ? error.response.data.message
          : "Network error";
      setAlert({ msg: "Login failed: " + errorMessage, type: "danger" });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isAuthenticated) return <Redirect to="/" />;

  return (
    <>
      <Helmet>
        <title>Real Estate - Login</title>
        <meta name="description" content="login page" />
      </Helmet>

      <div className="flex items-center justify-center min-h-[85vh] bg-gray-50 px-3 py-6">
        {/* Floating Alert Message */}
        {alert && (
          <div
            className={`max-w-md fixed top-16 w-11/12 p-3 text-white rounded-lg shadow-lg flex justify-between items-center text-xs font-medium ${
              alert.type === "success" ? "bg-emerald-600" : "bg-red-600"
            }`}
            style={{ zIndex: 1000 }}
          >
            <span>{alert.msg}</span>
            <button
              onClick={() => setAlert(null)}
              className="text-white hover:text-gray-200 ml-2 font-bold text-sm"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        )}

        <div className="max-w-sm w-full p-5 bg-white rounded-2xl shadow-xl border border-gray-100">
          {/* Lock Icon Badge */}
          <div className="flex justify-center mb-2">
            <div className="w-10 h-10 rounded-full bg-[#fdf0f3] flex items-center justify-center">
              <FaLock className="text-[#700c28] text-base" />
            </div>
          </div>

          {/* Title Header */}
          <div className="text-center mb-3">
            <h1 className="text-lg font-bold text-gray-900 leading-tight">Welcome Back!</h1>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Login to continue your real estate journey
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={onSubmit} className="space-y-2.5">
            <div>
              <label className="block text-xs font-semibold text-gray-800 mb-1">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                className="w-full py-1.5 px-2.5 border border-gray-300 rounded-lg text-xs text-gray-800 bg-white placeholder-gray-400 focus:outline-none focus:border-[#700c28]"
                placeholder="Enter your email"
                name="email"
                value={email}
                onChange={onChange}
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-800 mb-1">
                Password <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                className="w-full py-1.5 px-2.5 border border-gray-300 rounded-lg text-xs text-gray-800 bg-white placeholder-gray-400 focus:outline-none focus:border-[#700c28]"
                placeholder="Enter your password"
                name="password"
                value={password}
                minLength="6"
                onChange={onChange}
                required
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-2 mt-1 rounded-lg font-semibold text-white transition-all shadow-sm text-xs ${
                isSubmitting
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-[#700c28] hover:bg-[#580920] active:scale-[0.99]"
              }`}
            >
              {isSubmitting ? "SIGNING IN..." : "Login"}
            </button>
          </form>

          {/* OR Divider */}
          <div className="relative flex py-2.5 items-center">
            <div className="flex-grow border-t border-gray-200"></div>
            <span className="flex-shrink mx-3 text-[10px] font-medium text-gray-400 tracking-wider">
              OR
            </span>
            <div className="flex-grow border-t border-gray-200"></div>
          </div>

          <p className="text-center text-[10px] font-medium text-gray-500 mb-1.5">
            Login with
          </p>

          {/* Social Login Row */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <button
              type="button"
              className="flex items-center justify-center gap-1.5 py-1.5 px-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-xs font-semibold text-gray-700"
            >
              <FcGoogle size={15} />
              <span>Google</span>
            </button>
            <button
              type="button"
              className="flex items-center justify-center gap-1.5 py-1.5 px-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-xs font-semibold text-gray-700"
            >
              <FaApple size={15} className="text-black" />
              <span>Apple</span>
            </button>
          </div>

          {/* Feature Highlights Box */}
          <div className="bg-[#fdf0f3] rounded-xl p-2.5 mb-3 border border-[#f7d6de]">
            <div className="flex items-center gap-1.5 mb-1.5">
              <IoShieldCheckmarkOutline className="text-[#700c28] text-base" />
              <h4 className="font-bold text-xs text-[#700c28]">
                Why login to ZeroBriks?
              </h4>
            </div>

            <div className="grid grid-cols-2 gap-x-1.5 gap-y-1 text-[10px] text-gray-700 font-medium">
              <div className="flex items-center gap-1">
                <FiCheck className="text-emerald-600 flex-shrink-0" size={12} />
                <span className="truncate">Save favourite properties</span>
              </div>
              <div className="flex items-center gap-1">
                <FiCheck className="text-emerald-600 flex-shrink-0" size={12} />
                <span className="truncate">Manage your properties</span>
              </div>
              <div className="flex items-center gap-1">
                <FiCheck className="text-emerald-600 flex-shrink-0" size={12} />
                <span className="truncate">Personalised recommendations</span>
              </div>
              <div className="flex items-center gap-1">
                <FiCheck className="text-emerald-600 flex-shrink-0" size={12} />
                <span className="truncate">Access exclusive offers</span>
              </div>
              <div className="flex items-center gap-1">
                <FiCheck className="text-emerald-600 flex-shrink-0" size={12} />
                <span className="truncate">Track enquiries</span>
              </div>
              <div className="flex items-center gap-1">
                <FiCheck className="text-emerald-600 flex-shrink-0" size={12} />
                <span className="truncate">Faster & secure experience</span>
              </div>
            </div>
          </div>

          {/* Footer Signup Link */}
          <p className="text-center text-xs text-gray-600 font-medium">
            Don't have an account?{" "}
            <Link to="/signup" className="text-[#700c28] font-bold hover:underline">
              Sign Up
            </Link>
          </p>
        </div>
      </div>
    </>
  );
};

Login.propTypes = {
  login: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  isAuthenticated: state.auth.isAuthenticated,
});

export default connect(mapStateToProps, { login })(Login);